import { useEffect, useRef, useState } from "react";
import {
  Card,
  Form,
  Grid,
  Input,
  Loader,
  Button,
  Select,
  Modal,
  Icon,
  Popup,
} from "semantic-ui-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import FullCalendar from "@fullcalendar/react";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import interactionPlugin, { Draggable } from "@fullcalendar/interaction";
import "./dailyView.css";
import { apiScheduleJob } from "../jobScheduling.api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import JobSchedulingSearch from "../Search";
import * as jobSchedulingReducer from "../jobScheduling.reducer";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import Autocomplete from "react-google-autocomplete";
import { padZero } from "../../../utils/utils";

const DailyView = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [formData, setFormData] = useState({});
  const [jobListData, setJobListData] = useState(props.unscheduledJobs);
  const [events, setEvents] = useState(props.scheduledJobs);
  const [selectedDay, setSelectedDay] = useState(
    props.selectedDay ? props.selectedDay : new Date()
  );
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [openScheduleJob, setOpenScheduleJob] = useState(false);
  const [draggableCards, setDraggableCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState("");
  const calendarRef = useRef(null);
  const initialLoadRef = useRef(true);
  const currentDroppedResourceId = useRef(null);
  const externalListRef = useRef(null);
  const externalEventsRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [insertJobCard, setInsertJobCard] = useState(false);
  const [draggedJobCard, setDraggedJobCard] = useState(false);
  const dragCoordsRef = useRef({ clientX: 0, clientY: 0 });

  const locationRef = useRef(null);

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMoveDuringDrag);
    };
  }, []);

  useEffect(() => {
    if (props.scheduledJobs) setEvents(props.scheduledJobs);
  }, [props.scheduledJobs]);

  useEffect(() => {
    if (props.scheduledJobs) setJobListData(props.unscheduledJobs);
  }, [props.unscheduledJobs]);

  useEffect(() => {
    if (props.selectedDay) {
      setSelectedDay(props.selectedDay);
    }
  }, [props.selectedDay]);

  useEffect(() => {
    if (externalEventsRef.current) {
      new Draggable(externalEventsRef.current, {
        itemSelector: ".fc-event-draggable",
        eventData: function (eventEl) {
          const cardData = JSON.parse(eventEl.dataset.event);
          return {
            title: cardData.title,
            duration: cardData.duration,
            create: true,
            id: cardData.id,
            extendedProps: cardData.extendedProps,
            color: cardData.extendedProps.color || "#007bff",
          };
        },
      });
    }
  }, []);

  useEffect(() => {
    const userTechnician = document.querySelector(
      `[data-resource-id="${localStorage.getItem("user")}"]`
    );
    const unassignedContainer = document.querySelector(
      `[data-resource-id="Unassigned"]`
    );
    if (userTechnician) {
      const firstDivInsideTd = userTechnician.querySelector("div:first-child");
      const secondDivInsideTd =
        firstDivInsideTd.querySelector("div:first-child");

      if (secondDivInsideTd) {
        secondDivInsideTd.style.border = "2px solid #66cc23";
        secondDivInsideTd.style.borderRadius = "5px";
        secondDivInsideTd.style.paddingTop = "5px";
        secondDivInsideTd.style.paddingBottom = "5px";
        secondDivInsideTd.style.paddingLeft = "15px";
        secondDivInsideTd.style.paddingRight = "15px";
      }
    }
    if (unassignedContainer) {
      unassignedContainer.style.color = "#808080";
    }
  }, [events]);

  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      if (initialLoadRef.current) {
        calendarApi.gotoDate(selectedDay);
        if (selectedDay?.getDay() === new Date().getDay()) {
          const currentHour = new Date().getHours();
          const targetHour = currentHour - 1 < 0 ? 0 : currentHour - 1;
          const currentMinutes = new Date().getMinutes();
          calendarApi.scrollToTime(`${targetHour}:${currentMinutes}:00`);
        } else {
          calendarApi.scrollToTime(`06:00:00`);
        }
        initialLoadRef.current = false;
      }
    }
  }, [selectedDay]);

  const handleCalendarDateChange = (date) => {
    props.actionSetSelectedDay(date);

    const calendarApi = calendarRef.current.getApi();

    if (calendarApi) {
      calendarApi.gotoDate(date);
    }
  };

  const formatDateTimeLocal = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    const hours = d.getHours().toString().padStart(2, "0");
    const minutes = d.getMinutes().toString().padStart(2, "0");
    const seconds = d.getSeconds().toString().padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const handleEventReceive = (info) => {
    let receivedEvent = info.event.toPlainObject();

    let eventStart = receivedEvent.start;
    let eventEnd = receivedEvent.end;

    if (!eventStart || !eventEnd) {
      const startDate = new Date(eventStart);

      if (!eventEnd) {
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate());
        endDate.setHours(startDate.getHours() + 1);
        eventEnd = endDate;
      } else {
        const currentEndDate = new Date(endDate);
        eventEnd = currentEndDate;
      }

      const newEvent = {
        id: receivedEvent.id || String(Date.now()),
        title: receivedEvent.title,
        start: new Date(formatDateTimeLocal(eventStart)).toISOString(),
        end: new Date(formatDateTimeLocal(eventEnd)).toISOString(),
        allDay: receivedEvent.allDay,
        extendedProps: receivedEvent.extendedProps,
        backgroundColor: receivedEvent.backgroundColor || "#007bff",
        resourceId: currentDroppedResourceId.current,
      };
      setEvents((prevEvents) => [...prevEvents, newEvent]);
      setSelectedDate(new Date(formatDateTimeLocal(startDate)));
      setSelectedEndDate(new Date(formatDateTimeLocal(eventEnd)));
      setFormData(newEvent);
      setOpenScheduleJob(true);
    } else {
      receivedEvent.resourceId = currentDroppedResourceId.current
        ? currentDroppedResourceId.current
        : receivedEvent.extendedProps.technicianEmail
        ? receivedEvent.extendedProps.technicianEmail
        : receivedEvent.extendedProps.resourceId
        ? receivedEvent.resourceId
        : null;
      const startUtcString = info.event.start.toISOString().substring(0, 19);
      const endUtcString = info.event.end.toISOString().substring(0, 19);
      receivedEvent.start = startUtcString;
      receivedEvent.end = endUtcString;
      setFormData(receivedEvent);
      setTimeout(function () {
        onSubmitScheduleJob(receivedEvent);
      }, 500);
    }
  };

  const handleEventResize = (info) => {
    console.log(info.event.toPlainObject());
    let receivedEvent = info.event.toPlainObject();
    const startUtcString = info.event.start.toISOString().substring(0, 19);
    const endUtcString = info.event.end.toISOString().substring(0, 19);
    receivedEvent.start = startUtcString;
    receivedEvent.end = endUtcString;
    receivedEvent.resourceId = currentDroppedResourceId.current
      ? currentDroppedResourceId.current
      : receivedEvent.extendedProps.technicianEmail
      ? receivedEvent.extendedProps.technicianEmail
      : receivedEvent.extendedProps.resourceId
      ? receivedEvent.resourceId
      : null;
    setFormData(receivedEvent);
    setTimeout(function () {
      onSubmitScheduleJob(receivedEvent);
    }, 500);
  };

  const handleEventDrop = (info) => {
    const updatedEvents = events?.map((event) => {
      if (event.id === info.event.id) {
        return {
          ...event,
          start: formatDateTimeLocal(info.event.start),
          end: formatDateTimeLocal(info.event.end),
          allDay: info.event.allDay,
          resourceId: info.newResource?.id
            ? info.newResource?.id
            : event.resourceId,
        };
      }
      return event;
    });
    setEvents(updatedEvents);
  };

  const handleMouseMoveDuringDrag = (e) => {
    const listRect = externalListRef.current?.getBoundingClientRect();
    if (listRect) {
      const { clientX, clientY } = e;

      if (
        clientX >= listRect.left &&
        clientX <= listRect.right &&
        clientY >= listRect.top &&
        clientY <= listRect.bottom
      ) {
        setInsertJobCard(true);
      } else {
        setInsertJobCard(false);
      }
    }
  };

  const handleEventDragStart = (info) => {
    setIsDragging(true);
    document.addEventListener("mousemove", handleMouseMoveDuringDrag);
  };

  const handleEventDragStop = (info) => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleMouseMoveDuringDrag);
    const { jsEvent, event } = info;
    const { clientX, clientY } = jsEvent;

    if (externalListRef.current) {
      const listRect = externalListRef.current?.getBoundingClientRect();
      if (listRect) {
        if (
          clientX >= listRect.left &&
          clientX <= listRect.right &&
          clientY >= listRect.top &&
          clientY <= listRect.bottom
        ) {
          setDraggableCards((prevCards) => [
            ...prevCards,
            event.toPlainObject(),
          ]);
          setEvents((prevEvents) =>
            prevEvents.filter((evt) => evt.id !== event.id)
          );

          onSubmitUncheduleJob();
        }
      }
    }
  };

  const handleEventDidMount = (info) => {
    info.el.setAttribute("draggable", true);
    setFormData(info.event.toPlainObject());
    setDraggedJobCard(info.event.toPlainObject());
    info.el.addEventListener("dragstart", (e) => {
      const eventData = JSON.stringify(info.event.toPlainObject());
      e.dataTransfer.setData("text/plain", eventData);
      e.currentTarget.classList.add("is-dragging");
    });
  };

  const onSubmitScheduleJob = async (data = null) => {
    setIsLoading(true);
    try {
      const result = await apiScheduleJob(data ? data : formData);
      if (result) {
        setData(result);
        setJobListData(result.unscheduledJobs);
        setEvents(result.scheduledJobs);
        setOpenScheduleJob(false);
        showSuccessToast(
          <div>
            <i className="check icon" /> Job Scheduled Successfully
          </div>
        );
        await props.actionGetJobSchedulingList("", null);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (e) {
      showErrorToast("Something went wrong");
      setIsLoading(false);
    }
  };

  const onSubmitUncheduleJob = async () => {
    setIsLoading(true);
    let payload = formData;
    payload.start = null;
    payload.end = null;
    payload.resourceId = payload.extendedProps?.technicianEmail;
    try {
      const result = await apiScheduleJob(payload);
      if (result) {
        setData(result);
        setJobListData(result.unscheduledJobs);
        setEvents(result.scheduledJobs);
        setOpenScheduleJob(false);
        showSuccessToast(
          <div>
            <i className="check icon" /> Job Unscheduled Successfully
          </div>
        );
        await props.actionGetJobSchedulingList("", null);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (e) {
      showErrorToast("Something went wrong");
      setIsLoading(false);
    }
  };

  const handleChange = (name, value) => {
    if (name === "customerLocation") {
      if (locationRef && locationRef.current) {
        setFormData((prev) => ({
          ...prev,
          extendedProps: {
            customerLocation: locationRef.current.refs.input.value,
          },
        }));
      }
      return true;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "start") {
      setSelectedDate(value);
      if (value >= selectedEndDate) {
        setSelectedEndDate(null);
      }
    }
    if (name === "end") {
      if (new Date(value) <= new Date(selectedDate)) {
        value.setHours(selectedDate.getHours());
        value.setMinutes(selectedDate.getMinutes());
      }
      setSelectedEndDate(value);
    }
  };

  const onAddressSelection = (place) => {
    handleChange("customerLocation", locationRef.current.refs.input.value);
  };

  const handleDateClick = (info) => {};

  const handleDatesSet = (dateInfo) => {
    props.actionSetSelectedDay(new Date(dateInfo.start));
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      const currentHour = new Date().getHours();
      const targetHour = currentHour - 1 < 0 ? 0 : currentHour - 1;
      const currentMinutes = new Date().getMinutes();
      calendarApi.scrollToTime(`${targetHour}:${currentMinutes}:00`);
    }
  };

  const renderCustomEventContent = (arg) => {
    const eventBackgroundColor =
      arg.event.backgroundColor || arg.event.color || "#007bff";

    var startDate = new Date(arg.event.start);
    var endDate = new Date(arg.event.end);

    // Format start date
    var formattedDate = `${padZero(startDate.getDate())}/${padZero(
      startDate.getMonth() + 1
    )}/${startDate.getFullYear().toString().slice(2)}`;
    var formattedTime = `${padZero(startDate.getHours())}:${padZero(
      startDate.getMinutes()
    )}`;

    // Format end date
    var formattedEndDate = `${padZero(endDate.getDate())}/${padZero(
      endDate.getMonth() + 1
    )}/${endDate.getFullYear().toString().slice(2)}`;
    var formattedEndTime = `${padZero(endDate.getHours())}:${padZero(
      endDate.getMinutes()
    )}`;

    return (
      <Popup
        wide
        trigger={
          <div
            style={{
              border: `2px solid ${eventBackgroundColor}`,
              borderRight: `10px solid ${eventBackgroundColor}`,
              backgroundColor: "#fff",
              color: "#000",
              padding: 6,
              borderRadius: "3px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: 13,
              height: "100%",
              minHeight: 76,
              boxSizing: "border-box",
              cursor: "pointer",
            }}
            onClick={() => {
              if (arg.event.id) {
                window.location.href = `/jobbing/${arg.event.id}`;
              } else {
                console.warn(
                  "Event ID is missing, cannot navigate to jobbing page."
                );
              }
            }}
          >
            {(arg.view.type === "timeGridDay" ||
              arg.view.type === "timeGridWeek" ||
              arg.view.type === "resourceTimelineDayWithTime") &&
              arg.timeText && (
                <span style={{ marginRight: "5px", fontWeight: "bold" }}>
                  {arg.timeText}
                </span>
              )}
            {arg.event.title} <br />
            <span>{arg.event.extendedProps.companyName}</span>
            <br />
            <span>{arg.timeText}</span>
          </div>
        }
        content={
          <div>
            Job #{arg.event.title} <br />
            <span>Customer: {arg.event.extendedProps.companyName}</span>
            <br />
            <span>Address: {arg.event.extendedProps?.customerLocation}</span>
            <br />
            <span>Start Time: {`${formattedDate} ${formattedTime}`}</span>
            <br />
            <span>End Time: {`${formattedEndDate} ${formattedEndTime}`}</span>
          </div>
        }
        position="top center"
      />
    );
  };

  const isSameDayNative = (date1, date2) => {
    if (!date1 || !date2) return false;
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const getStartOfDay = (date) => {
    if (!date) return null;
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  };

  const getEndOfDay = (date) => {
    if (!date) return null;
    const newDate = new Date(date);
    newDate.setHours(23, 59, 59, 999);
    return newDate;
  };

  const getMinTimeForSelectedEndDate = () => {
    if (!selectedDate || !selectedEndDate) {
      return null;
    }

    if (isSameDayNative(selectedDate, selectedEndDate)) {
      return selectedDate;
    }

    return getStartOfDay(new Date());
  };

  const getMaxTimeForSelectedEndDate = () => {
    if (!selectedEndDate) {
      return null;
    }

    if (!selectedDate || !isSameDayNative(selectedDate, selectedEndDate)) {
      return getEndOfDay(selectedEndDate);
    }

    return getEndOfDay(selectedEndDate);
  };

  return (
    <div className="daily-view-container">
      <Modal
        className="job-schedule-form-modal"
        size="tiny"
        open={openScheduleJob}
        style={{ top: "20%" }}
      >
        <Modal.Header>Schedule Job</Modal.Header>
        <div style={{ position: "absolute", top: 200, left: 242 }}>
          {isLoading ? (
            <Loader
              active
              className="workaround"
              size="large"
              inline="centered"
            />
          ) : null}
        </div>
        <Modal.Content
          style={{
            opacity: isLoading ? 0.2 : 1,
            pointerEvents: isLoading ? "none" : "",
          }}
        >
          <Form size="tiny" style={{ fontSize: 14 }}>
            <Grid style={{ minHeight: 200 }} disabled>
              <div
                className="nonBOAProduct-form-content"
                style={{
                  paddingRight: "0px !important",
                  minHeight: 380,
                  maxHeight: 600,
                }}
              >
                <Form size="tiny">
                  <Grid>
                    <Grid.Row style={{ paddingBottom: 0, paddingTop: 35 }}>
                      <Grid.Column width={16} style={{ paddingBottom: 15 }}>
                        <Form.Field disabled>
                          <label>Job Number</label>
                          <Input value={`#${formData?.title}`} />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column
                        width={16}
                        style={{
                          paddingBottom: 15,
                        }}
                      >
                        <Form.Field disabled>
                          <label>Customer</label>
                          <Input value={formData?.extendedProps?.companyName} />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column
                        width={16}
                        style={{
                          paddingBottom: 15,
                        }}
                      >
                        <Form.Field>
                          <label>Address</label>
                          <Autocomplete
                            ref={locationRef}
                            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                            style={{ width: "100%" }}
                            placeholder=""
                            types={["geocode"]}
                            componentRestrictions={{
                              country: JSON.parse(
                                localStorage.getItem("countries")
                              ),
                            }}
                            value={formData?.extendedProps?.customerLocation}
                            onPlaceSelected={(place, inputRef) => {
                              onAddressSelection(place);
                            }}
                            onChange={(e, data) =>
                              handleChange("customerLocation", data)
                            }
                          />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column
                        width={16}
                        style={{
                          paddingBottom: 15,
                        }}
                      >
                        <Form.Field>
                          <label>Technician</label>
                          <Select
                            search
                            options={props.resources}
                            value={formData?.resourceId}
                            onChange={(e, data) =>
                              handleChange("resourceId", data.value)
                            }
                          />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column
                        width={8}
                        disabled={formData?.jobStatusId > 2}
                      >
                        <Form.Field>
                          <label>Start Time</label>
                          <DatePicker
                            dateFormat="dd/MM/yy HH:mm"
                            timeFormat="HH:mm"
                            selected={selectedDate}
                            showTimeSelect
                            timeIntervals={15}
                            onChange={(date) => handleChange("start", date)}
                          />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column
                        width={8}
                        disabled={formData?.jobStatusId > 2}
                      >
                        <Form.Field>
                          <label>End Time</label>
                          <DatePicker
                            disabled={!selectedDate}
                            minDate={selectedDate ? selectedDate : new Date()}
                            minTime={getMinTimeForSelectedEndDate()}
                            maxTime={getMaxTimeForSelectedEndDate()}
                            dateFormat="dd/MM/yy HH:mm"
                            timeFormat="HH:mm"
                            selected={selectedEndDate}
                            showTimeSelect
                            timeIntervals={15}
                            onChange={(date) => handleChange("end", date)}
                          />
                        </Form.Field>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Form>
              </div>
            </Grid>
            <Grid style={{ marginTop: 50 }}>
              <Grid.Column width={8} style={{ paddingRight: 5 }}>
                <Button
                  size="tiny"
                  style={{
                    width: "100%",
                    marginLeft: "auto",
                  }}
                  onClick={() => {
                    setEvents((prevEvents) =>
                      prevEvents.filter((event) => event.id !== formData.id)
                    );
                    setOpenScheduleJob(false);
                  }}
                >
                  CANCEL
                </Button>
              </Grid.Column>
              <Grid.Column width={8} style={{ paddingLeft: 5 }}>
                <Button
                  disabled={!selectedDate || !selectedEndDate}
                  size="tiny"
                  style={{
                    width: "100%",
                    backgroundColor: "rgb(102, 204, 35)",
                    color: "#fff",
                    marginLeft: "auto",
                  }}
                  onClick={() => {
                    onSubmitScheduleJob();
                  }}
                >
                  SCHEDULE
                </Button>
              </Grid.Column>
            </Grid>
          </Form>
        </Modal.Content>
      </Modal>
      <Grid columns={2} divided>
        <Grid.Column
          computer={4}
          tablet={6}
          mobile={16}
          className="job-list-section"
          style={{ paddingRight: 0 }}
        >
          <JobSchedulingSearch />
          <div className="job-list">
            <div style={{ position: "absolute", top: 300, left: 95 }}>
              {props.searchLoading ? (
                <Loader
                  active
                  className="workaround"
                  size="large"
                  inline="centered"
                >
                  Loading Job Cards...
                </Loader>
              ) : null}
            </div>
            {jobListData?.length === 0 && !props.searchLoading ? (
              <div>
                <p
                  style={{
                    textAlign: "center",
                    marginTop: 25,
                    fontSize: 20,
                  }}
                >
                  No unscheduled jobs
                </p>
              </div>
            ) : null}
            <div
              ref={externalListRef}
              className="job-list-scroll"
              disabled={props.searchLoading}
              style={{ opacity: props.searchLoading ? 0.2 : 1 }}
            >
              <div ref={externalEventsRef}>
                {insertJobCard && isDragging && (
                  <Card
                    key={draggedJobCard.id}
                    className={
                      selectedCard === draggedJobCard.jobNumber
                        ? "schedule-card"
                        : "schedule-card"
                    }
                    style={{ opacity: 0.3 }}
                  >
                    <Card.Content style={{ cursor: "pointer" }}>
                      <Grid columns={2}>
                        <Grid.Row>
                          <Grid.Column
                            width={14}
                            style={{ paddingTop: 6, paddingBottom: 6 }}
                          >
                            <b>
                              <Card.Description>
                                Job #{draggedJobCard.title}
                              </Card.Description>
                            </b>
                            <Card.Description>
                              Customer:{" "}
                              {draggedJobCard.extendedProps?.companyName ===
                              "-PLACEHOLDER-"
                                ? ""
                                : draggedJobCard.extendedProps?.companyName}
                            </Card.Description>
                            <Card.Description>
                              Address:{" "}
                              {draggedJobCard.extendedProps?.customerLocation}
                            </Card.Description>
                            <Card.Description>
                              Technician:{" "}
                              {draggedJobCard.extendedProps?.technicianName}
                            </Card.Description>
                          </Grid.Column>
                          <Grid.Column
                            width={2}
                            style={{
                              padding: 0,
                              backgroundColor: draggedJobCard.borderColor,
                            }}
                          >
                            <div className="job-state">
                              <span className="job-state-text">
                                {draggedJobCard.extendedProps?.jobStatusId === 1
                                  ? "New Request"
                                  : draggedJobCard.extendedProps
                                      ?.jobStatusId === 2
                                  ? "In Progress"
                                  : draggedJobCard.extendedProps
                                      ?.jobStatusId === 3
                                  ? "Completed"
                                  : "Invoiced"}
                              </span>
                            </div>
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Card.Content>
                  </Card>
                )}
                {jobListData?.map((data, i) => {
                  return (
                    <Card
                      key={data.id}
                      className={
                        selectedCard === data.jobNumber
                          ? "schedule-card active fc-event-draggable"
                          : "schedule-card fc-event-draggable"
                      }
                      data-event={JSON.stringify({
                        title: data.title,
                        duration: data.duration,
                        create: true,
                        id: data.id,
                        extendedProps: data,
                      })}
                    >
                      <Card.Content style={{ cursor: "pointer" }}>
                        <Grid columns={2}>
                          <Grid.Row>
                            <Grid.Column
                              width={14}
                              style={{ paddingTop: 6, paddingBottom: 6 }}
                            >
                              <b>
                                <Card.Description>
                                  Job #{data.jobNumber}
                                </Card.Description>
                              </b>
                              <Card.Description>
                                Customer:{" "}
                                {data.companyName === "-PLACEHOLDER-"
                                  ? ""
                                  : data.companyName}
                              </Card.Description>
                              <Card.Description>
                                Address: {data.customerLocation}
                              </Card.Description>
                              <Card.Description>
                                Technician: {data.technicianName}
                              </Card.Description>
                            </Grid.Column>
                            <Grid.Column
                              width={2}
                              style={{
                                padding: 0,
                                backgroundColor:
                                  data.jobStatusId === 1
                                    ? "#ff8f8f"
                                    : data.jobStatusId === 2
                                    ? "#ffdda6"
                                    : data.jobStatusId === 4
                                    ? "#71a8d2"
                                    : data.jobStatusId === 0 ||
                                      data.jobStatusId === 5
                                    ? "#ced4db"
                                    : "#c4f8c8",
                              }}
                            >
                              <div className="job-state">
                                <span className="job-state-text">
                                  {data.jobStatusId === 1
                                    ? "New Request"
                                    : data.jobStatusId === 2
                                    ? "In Progress"
                                    : data.jobStatusId === 3
                                    ? "Completed"
                                    : "Invoiced"}
                                </span>
                              </div>
                            </Grid.Column>
                          </Grid.Row>
                        </Grid>
                      </Card.Content>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </Grid.Column>
        <Grid.Column
          width={12}
          style={{ paddingRight: 0, overflow: "visible", paddingRight: 23 }}
        >
          <div style={{ flex: 3, position: "relative" }}>
            <div style={{ position: "absolute", top: 0, left: 0, zIndex: 98 }}>
              <Form>
                <Form.Field style={{ width: 200 }}>
                  <DatePicker
                    selected={selectedDay}
                    onChange={handleCalendarDateChange}
                    dateFormat="d MMMM yyyy"
                    className="semantic-ui-react-datepicker" // Ensure this is present
                    popperPlacement="bottom-start"
                  />
                </Form.Field>
              </Form>
            </div>
            <div
              style={{
                height: 700,
                maxHeight: 700,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <FullCalendar
                ref={calendarRef}
                height={props.resources?.length > 6 ? 700 : "auto"}
                plugins={[resourceTimelinePlugin, interactionPlugin]}
                schedulerLicenseKey={
                  process.env.REACT_APP_FULL_CALENDAR_LICENSE_KEY
                }
                resources={props.resources}
                resourceOrder={""}
                events={events}
                editable={true}
                eventDidMount={handleEventDidMount}
                selectable={true}
                droppable={true}
                resourceAreaWidth="150px"
                slotMinWidth={58.25}
                resourceAreaHeaderContent="Technician"
                initialView="resourceTimelineDayWithTime"
                headerToolbar={{
                  left: "prev,next today",
                  center: false,
                  right: false,
                }}
                buttonText={{
                  today: "Today",
                  resourceTimelineDayWithTime: "Day",
                }}
                firstDay={1}
                nowIndicator={true}
                scrollTime={false}
                scrollTimeReset={false}
                views={{
                  resourceTimelineDayWithTime: {
                    type: "resourceTimelineDay",
                    slotLabelFormat: {
                      hour: "2-digit",
                      minute: "2-digit",
                      hourCycle: "h23",
                    },
                    eventTimeFormat: {
                      hour: "2-digit",
                      minute: "2-digit",
                      hourCycle: "h23",
                    },
                    slotMinHeight: 500,
                  },
                }}
                eventMinHeight={300}
                eventResize={handleEventResize}
                eventReceive={handleEventReceive}
                eventDrop={handleEventDrop}
                eventDragStart={handleEventDragStart}
                eventDragStop={handleEventDragStop}
                drop={(info) => {
                  if (info.resource) {
                    currentDroppedResourceId.current = info.resource.id;
                  } else {
                    currentDroppedResourceId.current = null;
                  }
                }}
                eventContent={renderCustomEventContent}
                dateClick={handleDateClick}
                datesSet={handleDatesSet}
              />
            </div>
          </div>
        </Grid.Column>
      </Grid>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    scheduledJobs: state.jobScheduling.scheduledJobs,
    unscheduledJobs: state.jobScheduling.unscheduledJobs,
    selectedDay: state.jobScheduling.selectedDay,
  };
};
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(jobSchedulingReducer, dispatch);

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(DailyView)
);
